/**
 * AttachmentPreview - Rich file cards with disk preview thumbnails.
 *
 * Uses disk_file_id to fetch thumbnails from the Disk processing pipeline
 * via GET /api/processing/{file_id}/preview.
 */

import React, { useState, useEffect } from 'react';
import type { ChatAttachment } from '../../types/chat';
import { getPreviewUrl } from '../../services/filesApi';
import { chatAttachmentsApi } from '../../services/chatApi';

interface AttachmentPreviewProps {
  attachment: ChatAttachment;
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
}

function getFileIcon(contentType: string, fileName: string): string {
  if (contentType.startsWith('image/')) return '\u{1F5BC}';
  if (contentType.startsWith('video/')) return '\u{1F3AC}';
  if (contentType.startsWith('audio/')) return '\u{1F3B5}';
  if (contentType === 'application/pdf') return '\u{1F4C4}';
  const ext = fileName.split('.').pop()?.toLowerCase() || '';
  if (['doc', 'docx'].includes(ext)) return '\u{1F4DD}';
  if (['xls', 'xlsx'].includes(ext)) return '\u{1F4CA}';
  if (['ppt', 'pptx'].includes(ext)) return '\u{1F4CA}';
  if (['zip', 'rar', '7z', 'tar', 'gz'].includes(ext)) return '\u{1F4E6}';
  return '\u{1F4C3}';
}

const AttachmentPreview: React.FC<AttachmentPreviewProps> = ({ attachment }) => {
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [loadingPreview, setLoadingPreview] = useState(false);

  const isImage = attachment.content_type.startsWith('image/');
  const isPreviewable = isImage
    || attachment.content_type === 'application/pdf'
    || /\.(docx?|xlsx?|pptx?)$/i.test(attachment.file_name);

  // Fetch preview thumbnail from disk processing pipeline
  useEffect(() => {
    if (!attachment.disk_file_id || !isPreviewable) return;
    setLoadingPreview(true);
    const size = isImage ? 'md' : 'sm';
    getPreviewUrl(attachment.disk_file_id, size)
      .then(r => setPreviewUrl(r.preview_url))
      .catch(() => {}) // preview not ready yet
      .finally(() => setLoadingPreview(false));
  }, [attachment.disk_file_id, isPreviewable, isImage]);

  const handleDownload = async () => {
    try {
      const { url } = await chatAttachmentsApi.getDownloadUrl(attachment.id);
      window.open(url, '_blank');
    } catch (err) {
      console.error('Download failed:', err);
    }
  };

  // Image with preview
  if (isImage) {
    return (
      <div
        className="rounded-lg border overflow-hidden max-w-xs cursor-pointer hover:shadow-md transition-shadow"
        onClick={handleDownload}
      >
        {previewUrl ? (
          <img
            src={previewUrl}
            alt={attachment.file_name}
            className="max-h-48 w-auto object-contain bg-gray-50"
          />
        ) : loadingPreview ? (
          <div className="h-32 flex items-center justify-center bg-gray-50">
            <div className="w-5 h-5 border-2 border-blue-400 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <div className="h-24 flex items-center justify-center bg-gray-100">
            <span className="text-2xl">{getFileIcon(attachment.content_type, attachment.file_name)}</span>
          </div>
        )}
        <div className="px-2 py-1.5 bg-white border-t">
          <div className="text-xs text-gray-700 truncate">{attachment.file_name}</div>
          <div className="text-xs text-gray-400">{formatFileSize(attachment.file_size)}</div>
        </div>
      </div>
    );
  }

  // PDF / Office docs with thumbnail
  if (isPreviewable) {
    return (
      <div
        className="flex items-center gap-3 rounded-lg border px-3 py-2 bg-white max-w-xs cursor-pointer hover:shadow-md transition-shadow"
        onClick={handleDownload}
      >
        {previewUrl ? (
          <img
            src={previewUrl}
            alt={attachment.file_name}
            className="w-10 h-14 object-cover rounded border bg-gray-50 flex-shrink-0"
          />
        ) : (
          <div className="w-10 h-14 flex items-center justify-center rounded border bg-gray-50 flex-shrink-0 text-lg">
            {getFileIcon(attachment.content_type, attachment.file_name)}
          </div>
        )}
        <div className="min-w-0">
          <div className="text-sm text-gray-800 truncate">{attachment.file_name}</div>
          <div className="text-xs text-gray-400">{formatFileSize(attachment.file_size)}</div>
        </div>
      </div>
    );
  }

  // Generic file
  return (
    <div
      className="flex items-center gap-2 rounded-lg border px-3 py-2 bg-gray-50 max-w-xs cursor-pointer hover:shadow-md transition-shadow"
      onClick={handleDownload}
    >
      <div className="text-lg flex-shrink-0">
        {getFileIcon(attachment.content_type, attachment.file_name)}
      </div>
      <div className="min-w-0">
        <div className="text-sm text-gray-800 truncate">{attachment.file_name}</div>
        <div className="text-xs text-gray-400">{formatFileSize(attachment.file_size)}</div>
      </div>
    </div>
  );
};

export default AttachmentPreview;
